import { BehaviorSubject } from "rxjs";
import { AllowedKeyType, KeyDefinitionType, KeyValueType } from "../types/key-types";

type MapFn<TModel, TMapped> = (item: TModel, idx?: number, arr?: TModel[]) => TMapped;
/**
 * since RemoteCollectionStores are stateful they will fetch and keep data in memory.
 * this means when data is fetched from the remote service we need to store this dataset as is and do not alter it
 * LocalCollectionStore provides an extra layer of complexity between RemoteCollectionStore and consumer of remote collection store
 */
export class LocalCollectionStore<TModel extends { [key: string]: any }, TMapped extends { [key: string]: any } = TModel, TKeyType extends AllowedKeyType = string> {

  readonly stateSubject = new BehaviorSubject<TMapped[]>(null);

  private _keyField: KeyDefinitionType<TKeyType>

  get keyField() {
    return this._keyField
  }

  set keyField(value: KeyDefinitionType<TKeyType>) {
    this._keyField = value;
  }

  private _map: MapFn<TModel, TMapped>
  get map() {
    return this._map
  }
  set map(value: MapFn<TModel, TMapped>) {
    this._map = value;
  }



  constructor() {


  }

  configure(keyField: KeyDefinitionType<TKeyType>, map?: MapFn<TModel, TMapped>) {
    this.keyField = keyField
    this.map = map;
  }

  /**replaces entire item dataset */
  refresh(newData: TModel[]) {
    if (!this._map) {
      this.stateSubject.next(newData as unknown as TMapped[]);
      return;
    }

    this.stateSubject.next(newData.map(this._map))
  }

  //** adds one item */
  addOne(added: TModel) {

    const newState = [...this.stateSubject.value];
    const addedMapped = this.mapOne(added);
    newState.push(addedMapped);
    this.stateSubject.next(newState);
  }

  //** adds many items */
  addMany(added: TModel[]) {

    const newState = [...this.stateSubject.value] || [];
    const addedMapped = this.mapMany(added);

    newState.push(...addedMapped);

    this.stateSubject.next(newState);
  }

  removeOne(key: TKeyType) {

    const prevState = this.stateSubject.value
    const newState = prevState.filter((item) => !this._compareModelAndKey(key, item))

    this.stateSubject.next(newState);
  }

  removeMany(keyArr: TKeyType[]) {

    const newState = this.stateSubject.value.filter((item) => !keyArr.find((key) => this._compareModelAndKey(key, item)))
    this.stateSubject.next(newState);

  }

  updateOne(key: TKeyType, model: TModel) {

    const newState = [...this.stateSubject.value];
    const idx = newState.findIndex((item) => this._compareModelAndKey(key, item))
    newState[idx] = this.mapOne(model);

    this.stateSubject.next(newState);

  }

  updateMany(updatePairs: { key: TKeyType, model: TModel }[]) {
    const newState = [...this.stateSubject.value];

    updatePairs
      .map(({ key, model }) => ({
        model: this.mapOne(model),
        idx: newState.findIndex((item) => this._compareModelAndKey(key, item))
      }))
      .filter(({ idx }) => idx >= 0)
      .forEach((({ idx, model }) => {
        newState[idx] = model;
      }))

    this.stateSubject.next(newState)
  }

  find(key: TKeyType) {

    const state = this.stateSubject.value;

    const found = state.find((item) => this._compareModelAndKey(key, item));

    return found;
  }

  private _compareModelAndKey(key: TKeyType, model: TMapped) {

    const keyField = this._keyField;
    const isKeySimple = typeof keyField === 'string'

    if (isKeySimple) {

      return model[keyField as any] === key;
    }

    const complexKey = key as KeyValueType<'complex'>

    return (<string[]>keyField).reduce((equal, keyPart) => equal && model[keyPart] === complexKey[keyPart], true)
  }

  public mapOne(item: TModel) {

    return this._map ? this._map(item) : item as unknown as TMapped
  }

  public mapMany(items: TModel[]) {

    return this._map ? items.map(this._map) : items as unknown as TMapped[]

  }

}
